import styles from '../styles/EventStatsModal.module.css';
import { trpc } from '../utils/trpc';

// Helpers

export function attendanceRate(count: number, cap: number): number {
  if (!cap) return 0;
  return Math.round((count / cap) * 100);
}

export function satisfactionScore(likes: number, dislikes: number): number {
  const total = likes + dislikes;
  if (total === 0) return 0;
  return Math.round((likes / total) * 100);
}

export function rsvpRate(rsvpCount: number, cap: number): number {
  if (!cap) return 0;
  return Math.round((rsvpCount / cap) * 100);
}

// Reusable Stat Tile

export function StatTile({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string | number;
  sub?: string;
  accent?: 'green' | 'gold' | 'orange';
}) {
  return (
    <div className={`${styles.tile} ${accent ? styles[`tile_${accent}`] : ''}`}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={styles.tileValue}>{value}</span>
      {sub && <span className={styles.tileSub}>{sub}</span>}
    </div>
  );
}

interface OrganizerEventStatisticsModalProps {
  eventId: string;
  eventName: string;
  onClose: () => void;
}

export function OrganizerEventStatisticsModal({
  eventId,
  eventName,
  onClose,
}: OrganizerEventStatisticsModalProps) {
  // Queries
  const rsvpQuery = trpc.event.getRsvpStats.useQuery({ event_id: eventId });
  const eventQuery = trpc.event.getEventById.useQuery({ eventId });
  const tagsQuery = trpc.event.getVisitorTags.useQuery({ event_id: eventId });
  const attendedQuery = trpc.event.getAttendedCount.useQuery({ eventId });
  const ratingQuery = trpc.event.getRatingStats.useQuery({ eventId });

  // Loading State
  const isLoading =
    rsvpQuery.isLoading ||
    eventQuery.isLoading ||
    tagsQuery.isLoading ||
    attendedQuery.isLoading ||
    ratingQuery.isLoading;

  if (isLoading) {
    return (
      <div className={styles.backdrop}>
        <div className={styles.modal}>
          <p>Loading statistics...</p>
        </div>
      </div>
    );
  }

  const rsvpCount = rsvpQuery.data?.count ?? 0;
  const maxCapacity = eventQuery.data?.max_capacity ?? 0;
  const attendedCount = attendedQuery.data?.count ?? 0;
  const stats = ratingQuery.data ?? { likes: 0, dislikes: 0 };
  const commonTags = tagsQuery.data ?? [];

  const attendRate = attendanceRate(attendedCount, maxCapacity);
  const rsvpRateValue = rsvpRate(rsvpCount, maxCapacity);
  const satisfaction = satisfactionScore(stats.likes, stats.dislikes);

  function handleBackdrop(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === e.currentTarget) onClose();
  }

  return (
    <div
      className={styles.backdrop}
      onClick={handleBackdrop}
      role="dialog"
      aria-modal="true"
    >
      <div className={styles.modal}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.headerEyebrow}>Event Success</span>
            <h2 className={styles.headerTitle}>{eventName}</h2>
          </div>
          <button
            className={styles.closeBtn}
            onClick={onClose}
            aria-label="Close statistics"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className={styles.body}>
          {/* Grid Layout */}
          <div className={styles.tileGrid}>
            <StatTile
              label="Attendees"
              value={rsvpCount}
              sub={`of ${maxCapacity} capacity`}
              accent="green"
            />

            <StatTile
              label="Attendance Rate"
              value={`${attendRate}%`}
              sub={
                attendRate >= 80
                  ? 'Excellent turnout'
                  : attendRate >= 50
                    ? 'Good turnout'
                    : 'Low turnout'
              }
              accent={attendRate >= 80 ? 'green' : attendRate >= 50 ? 'gold' : 'orange'}
            />

            <StatTile
              label="Rsvp Rate"
              value={`${rsvpRateValue}%`}
              sub={
                rsvpRateValue >= 80
                  ? 'High interest'
                  : rsvpRateValue >= 50
                    ? 'Good turnout'
                    : 'Low turnout'
              }
              accent={
                rsvpRateValue >= 80 ? 'green' : rsvpRateValue >= 50 ? 'gold' : 'orange'
              }
            />

            <StatTile
              label="Satisfaction"
              value={`${satisfaction}%`}
              sub={`${stats.likes} 👍 · ${stats.dislikes} 👎`}
              accent={
                satisfaction >= 80 ? 'green' : satisfaction >= 50 ? 'gold' : 'orange'
              }
            />
          </div>

          {/* Tags */}
          <div className={styles.section}>
            <span className={styles.sectionLabel}>Common Attendee Interests</span>
            <div className={styles.tagList}>
              {commonTags.length > 0 ? (
                commonTags.map((tag) => (
                  <span key={tag.id} className={styles.tag}>
                    {tag.name}
                  </span>
                ))
              ) : (
                <p className={styles.emptyText}>No interest data available</p>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className={styles.section}>
            <span className={styles.sectionLabel}>Summary</span>
            <div className={styles.satisfactionCard}>
              <p className={styles.satisfactionNote}>
                {satisfaction >= 80
                  ? 'This event was very well received by attendees.'
                  : satisfaction >= 50
                    ? 'Attendees had a generally positive experience.'
                    : 'This event may need improvement based on feedback.'}
              </p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <button className={styles.btnClose} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
